import { Environment } from '@lfg/core/enums';
import { Web3Provider as LFGWeb3Provider } from '@lfg/core/providers/Web3Provider';
import { QueryClient } from '@tanstack/react-query';
import { PropsWithChildren, useMemo } from 'react';
import { createConfig, http } from 'wagmi';
import * as chains from 'wagmi/chains';

import { CHAINS_CONFIG } from 'src/utils/chainUtils';

import { ALCHEMY_KEY, APP_ENV } from '../global/config/common.config';
import { connectors } from '../global/config/wagmi.config';
import { useNetwork } from './NetworkProvider';
import Web3SignerBootstrap from './Web3SignerBootstrap';

export interface Web3ProviderProps {
  cookie?: string | null;
}

export const Web3Provider = ({ children }: PropsWithChildren<Web3ProviderProps>) => {
  const query = new QueryClient();
  const appInfo = { appName: 'NODE_SALE_ADMIN' };
  const { selectedNetwork, isNetworkInitialized } = useNetwork();

  const wagmiConfigV2 = useMemo(() => {
    // Wait until we have a selected network before creating config
    if (!selectedNetwork || !isNetworkInitialized) return null;

    const isProduction = APP_ENV === Environment.PROD;

    const transports = Object.fromEntries(
      Object.values(chains).map(chain => [
        chain.id,
        http(
          // For each chain, check if it matches the mainnet or testnet of our selected network
          // If in production mode, use mainnet RPC, otherwise use testnet RPC
          (isProduction && chain.id === CHAINS_CONFIG[selectedNetwork as keyof typeof CHAINS_CONFIG]?.mainnet?.id
            ? `${CHAINS_CONFIG[selectedNetwork as keyof typeof CHAINS_CONFIG]?.alchemyMainnet}/${ALCHEMY_KEY}`
            : !isProduction && chain.id === CHAINS_CONFIG[selectedNetwork as keyof typeof CHAINS_CONFIG]?.testnet?.id
              ? `${CHAINS_CONFIG[selectedNetwork as keyof typeof CHAINS_CONFIG]?.alchemyTestnet}/${ALCHEMY_KEY}`
              : '') || '',
        ),
      ]),
    );
    // Create the wagmi config with the selected network
    return createConfig({
      connectors,
      chains: Object.values(chains) as any,
      transports,
    });
  }, [selectedNetwork, isNetworkInitialized]);

  // Show loading state until configuration is ready
  if (!wagmiConfigV2) {
    return <div>Loading Web3 Provider...</div>;
  }

  return (
    <LFGWeb3Provider queryClient={query} rainbowKitProps={{ appInfo }} wagmiConfig={wagmiConfigV2}>
      {children}
      <Web3SignerBootstrap />
    </LFGWeb3Provider>
  );
};
